import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { Actividad, ActividadesPagingRes, BitacoraSoftnet, CalendarEventSeguimientoAct, TecnicosSoftnet } from './soft-seguimientoactividades-actions-type';
import { useUiStore } from '@/store/ui';

const { post, get } = erpApi();

///* tanStack query
export const useFetchSeguimientoActividadesSoftnet = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetSeguimientoActividadesParams> = {}) => {
  return useQuery({
    queryKey: ['seguimientoactividades-soft', ...Object.values(params ?? {})],
    queryFn: () => getSeguimientoActividades(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchTecnicosSoftnet = ({
  enabled = true,
}: UseFetchEnabledParams<TecnicosSoftnet[]> = {}) => {
  return useQuery({
    queryKey: ['tecnicos-soft'],
    queryFn: () => getTecnicosSoftnet(),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};


export const useFetchBitacoraSoftnet = ({
  navigate,

  enableRedirect = false,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = false,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: {id: number, tipo:number}) =>
      postGetBitacoraSoftnet(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bitacora-soft'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Bitacora obtenida correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};




export const useCreateRegistroBitacoraSoftnet = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateRegistroBitacoraSoftnetParams) =>
      postBitacoraSoftnet(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['create-registro-bitacora-softnet'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Mensaje enviado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};




///* axios
export type GetSeguimientoActividadesParams = Partial<Actividad> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;

  identificacion?: string;

  fecha_creacion_after?: string | null;
  fecha_creacion_before?: string | null
  contrato_estado_contrato?: string | null;
};


export type CreateRegistroBitacoraSoftnetParams = {
  id_instalacion: number;
  tipo_instalacion: number;
  comentario: string;
};


export const getSeguimientoActividades = async (params?: GetSeguimientoActividadesParams) => {
  const stateParams = { ...params };

  const queryParams = getUrlParamsFetch(stateParams);

  return post<ActividadesPagingRes>(
    `${urlBackend}api/v1/obtener-activaciones-pyl-sofnet/`,
    queryParams,
    true
  );
};




export const postPlanificadorFlotaSoftnet = async (numero_referencia: string, params: {anio: number, mes: number}) => {
  try {
    return await post<CalendarEventSeguimientoAct[]>(
      `${urlBackend}api/v1/obtener-agenda-tecnico-sofnet/${numero_referencia}/`,
      params,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};



export const postBitacoraSoftnet = async (params: {id_instalacion: number, tipo_instalacion: number, comentario: string}) => {
  try {
    return await post<CalendarEventSeguimientoAct[]>(
      `${urlBackend}api/v1/registrar-comentario-sofnet/`,
      params,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};


export const getTecnicosSoftnet = async () => {
  try {
    return await get<TecnicosSoftnet[]>(
      `${urlBackend}api/v1/obtener-tecnicos-externos/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};


export const postGetBitacoraSoftnet = async (params?: {id: number, tipo:number}) => {
  try {
    return await post<BitacoraSoftnet[]>(
      `${urlBackend}api/v1/obtener-seguimiento-pyl-sofnet/`,
      params,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};
